/**
 * Date Format Context
 * Provides global date format settings to all components
 */

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  DateFormatSettings,
  DateFormatType,
  TimeFormatType,
  getDateFormatSettings,
  saveDateFormatSettings,
  formatDate as formatDateUtil
} from '../lib/dateFormat';
import {
  CurrencyType,
  formatCurrency as formatCurrencyUtil,
  getCurrencySymbol
} from '../lib/currencyFormat';
import { apiGet, apiPut } from '../lib/api';
import { auth } from '../lib/auth';

interface DateFormatContextType {
  settings: DateFormatSettings;
  updateSettings: (settings: Partial<DateFormatSettings>) => Promise<void>;
  formatDate: (
    date: Date | string | number,
    options?: {
      includeTime?: boolean;
      format?: DateFormatType;
      timeFormat?: TimeFormatType;
    }
  ) => string;
  formatCurrency: (
    amount: number | string | null | undefined,
    options?: {
      currency?: CurrencyType;
      showSymbol?: boolean;
      minimumFractionDigits?: number;
      maximumFractionDigits?: number;
    }
  ) => string;
  getCurrencySymbol: (currency?: CurrencyType) => string;
}

const DateFormatContext = createContext<DateFormatContextType | undefined>(undefined);

export function DateFormatProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<DateFormatSettings>(getDateFormatSettings());

  // Load settings from database on mount
  useEffect(() => {
    const loadSettingsFromDatabase = async () => {
      try {
        // Start with localStorage for immediate UX
        const savedSettings = getDateFormatSettings();
        if (savedSettings && Object.keys(savedSettings).length > 0) {
          setSettings(savedSettings);
        }

        // Only load from API if authenticated
        if (!auth.getToken()) {
          return;
        }

        const result = await apiGet<any>('/settings/date-format');
        if (result.success && result.data && Object.keys(result.data).length > 0) {
          // Merge database settings with defaults
          const dbSettings = { ...getDateFormatSettings(), ...result.data };
          setSettings(dbSettings);
          // Also save to localStorage for offline access
          saveDateFormatSettings(result.data);
        }
      } catch (error) {
        console.error('Error loading date format settings from database:', error);
        // Fall back to localStorage settings
      }
    };

    loadSettingsFromDatabase();
  }, []);

  useEffect(() => {
    // Listen for changes from other tabs/windows or components
    const handleDateFormatChange = (event: CustomEvent<DateFormatSettings>) => {
      setSettings(event.detail);
    };

    window.addEventListener('dateFormatChanged', handleDateFormatChange as EventListener);

    return () => {
      window.removeEventListener('dateFormatChanged', handleDateFormatChange as EventListener);
    };
  }, []);

  const updateSettings = async (newSettings: Partial<DateFormatSettings>) => {
    const updated = { ...settings, ...newSettings };
    setSettings(updated);
    saveDateFormatSettings(newSettings);

    // Save to database if authenticated
    if (auth.getToken()) {
      try {
        await apiPut('/settings/date-format', updated);
      } catch (error) {
        console.error('Error saving date format settings to database:', error);
        // Settings still saved to localStorage, which is sufficient for offline use
      }
    }
  };

  const formatDate = (
    date: Date | string | number,
    options?: {
      includeTime?: boolean;
      format?: DateFormatType;
      timeFormat?: TimeFormatType;
    }
  ) => {
    return formatDateUtil(date, options);
  };

  const formatCurrency = (
    amount: number | string | null | undefined,
    options?: {
      currency?: CurrencyType;
      showSymbol?: boolean;
      minimumFractionDigits?: number;
      maximumFractionDigits?: number;
    }
  ) => {
    const currency = options?.currency || settings.currency || 'USD';
    return formatCurrencyUtil(amount, { ...options, currency });
  };

  return (
    <DateFormatContext.Provider value={{ settings, updateSettings, formatDate, formatCurrency, getCurrencySymbol }}>
      {children}
    </DateFormatContext.Provider>
  );
}

/**
 * Hook to use date format settings
 */
export function useDateFormat() {
  const context = useContext(DateFormatContext);
  if (!context) {
    throw new Error('useDateFormat must be used within a DateFormatProvider');
  }
  return context;
}

